import {
  KeyMomentCategory,
  CardColor,
  DEFAULT_KEY_MOMENT_LABELS,
} from './types';

/** API may use several codes for missed penalties / shootout misses — normalised labels in UI */
const PENALTY_MISS_MOMENTS = new Set([
  'PENALTY_MISSED',
  'PENALTY_SHOOTOUT_MISS',
  'PENALTY_SHOOTOUT_MISSED',
  'PENALTIES_MISS',
  'MISS_PENALTY',
  'MISSED_PENALTY',
]);

/**
 * Map alternate backend enums to canonical `keyMoments.moment.<id>` message ids,
 * without duplicating translations.
 */
// MATCH_START - Início da partida
// MATCH_END - Fim da partida
// GOAL - Gol normal
// YELLOW_CARD - Cartão amarelo
// RED_CARD_SECOND_YELLOW - Cartão vermelho (segundo amarelo)
// DIRECT_RED_CARD - Cartão vermelho direto
// END_FIRST_HALF - Fim do primeiro tempo
// START_SECOND_HALF - Início do segundo tempo
// SUBSTITUTION - Substituição
// PENALTY_GOAL - Gol de pênalti
// PENALTY_MISSED - Pênalti perdido
// END_SECOND_HALF - Fim do segundo tempo
// EXTRA_TIME_START - Início da prorrogação
// EXTRA_TIME_HALF_START - Início do segundo tempo da prorrogação
// EXTRA_TIME_HALF_END - Fim do primeiro tempo da prorrogação
// EXTRA_TIME_END - Fim da prorrogação
// PENALTY_SHOOTOUT_START - Início das cobranças de pênalti
// EVENT_DELETED - Evento deletad
export const KEY_MOMENT_MESSAGE_ALIASES = Object.freeze({
  END_FIRST_HALF: 'END_FIRST_HALF',

  START_SECOND_HALF: 'START_SECOND_HALF',

  END_SECOND_HALF: 'END_SECOND_HALF',

  EXTRA_TIME_START: 'EXTRA_TIME_START',

  EXTRA_TIME_HALF_END: 'EXTRA_TIME_HALF_END',

  EXTRA_TIME_HALF_START: 'EXTRA_TIME_HALF_START',

  EXTRA_TIME_END: 'EXTRA_TIME_END',

  PENALTY_SHOOTOUT_START: 'PENALTY_SHOOTOUT_START',
});

export function canonicalKeyMomentMomentCode(moment) {
  if (!moment) return '';
  return KEY_MOMENT_MESSAGE_ALIASES[moment] ?? moment;
}

export function isPenaltyMissMomentCode(moment) {
  return Boolean(moment && PENALTY_MISS_MOMENTS.has(moment));
}

/** Raw API moments with `moment === 'EVENT_DELETED'` — used only to hide linked incidents. */
export const EVENT_DELETED_MOMENT_CODE = 'EVENT_DELETED';

/** Accumulates `{ incidentId }` for deleted events; starts empty, deduped by `incidentId`. */
export const arrayEventDelete = [];

export function isEventDeletedMoment(moment) {
  return moment?.moment === EVENT_DELETED_MOMENT_CODE;
}

/**
 * Registers `EVENT_DELETED` from the raw API — reads only `incidentId`, ignores every other field.
 * @returns {boolean} whether a new `incidentId` was added
 */
export function registerEventDeletedMoments(moments) {
  const list = Array.isArray(moments) ? moments : [];
  let added = false;

  for (const moment of list) {
    if (isEventDeletedMoment(moment)) {
      const { incidentId } = moment;
      if (incidentId != null) {
        const alreadyRegistered = (arrayEventDelete ?? []).some(
          (entry) => entry.incidentId === incidentId,
        );
        if (!alreadyRegistered) {
          arrayEventDelete.push({ incidentId });
          added = true;
        }
      }
    }
  }

  return added;
}

export function resetArrayEventDelete() {
  arrayEventDelete.length = 0;
}

function isIncidentIdDeleted(incidentId) {
  return (arrayEventDelete ?? []).some((entry) => entry.incidentId === incidentId);
}

/** Hides carousel rows whose `incidentId` was marked deleted via `EVENT_DELETED`. */
export function filterCarouselKeyMoments(items) {
  const list = Array.isArray(items) ? items : [];
  const deleted = arrayEventDelete ?? [];

  if (deleted.length === 0) return list;

  return list.map((item) => (
    item != null && isIncidentIdDeleted(item.incidentId) ? null : item
  )).filter(Boolean);
}

function getCategory(moment) {
  if (moment === 'GOAL' || moment === 'PENALTY_GOAL' || moment === 'PENALTY_SHOOTOUT_GOAL'
    || moment === 'OWN_GOAL') {
    return KeyMomentCategory.GOAL;
  }
  if (isPenaltyMissMomentCode(moment)) {
    return KeyMomentCategory.GOAL;
  }
  if (
    moment === 'YELLOW_CARD'
    || moment === 'RED_CARD'
    || moment === 'RED_CARD_SECOND_YELLOW'
    || moment === 'DIRECT_RED_CARD'
    || moment === 'SECOND_YELLOW_RED_CARD'
  ) {
    return KeyMomentCategory.CARD;
  }
  if (moment === 'SUBSTITUTION') return KeyMomentCategory.SUBSTITUTION;
  return KeyMomentCategory.MATCH_STATUS;
}

function getCardColor(moment) {
  if (moment === 'YELLOW_CARD') return CardColor.YELLOW;
  if (
    moment === 'RED_CARD'
    || moment === 'RED_CARD_SECOND_YELLOW'
    || moment === 'DIRECT_RED_CARD'
    || moment === 'SECOND_YELLOW_RED_CARD'
  ) return CardColor.RED;
  // eslint-disable-next-line unicorn/no-useless-undefined
  return undefined;
}

export function normalizeApiMoments(moments) {
  return moments
    .filter((m) => !isEventDeletedMoment(m))
    .map((m) => {
      const category = getCategory(m.moment);
      let playerName;
      let playerNameIn;
      let score;

      if (m.goals) {
        playerName = m.goals.player;
        score = `${m.goals.homeTeamScore} - ${m.goals.awayTeamScore}`;
      } else if (m.cards && m.cards.length > 0) {
        playerName = m.cards[0].player;
      } else if (m.substitutions) {
        playerName = m.substitutions.out;
        playerNameIn = m.substitutions.in;
      }

      return {
        incidenceId: m.momentId,
        incidentId: m.incidentId,
        /** Backend moment code — used for match-status i18n keys. */
        moment: m.moment,
        category,
        half: m.currentPeriods,
        minutes: m.currentMinutes,
        seconds: m.currentSeconds,
        // Unix seconds (matches @dtvgo/progress-bar / dtvweb player seek offsets).
        timestamp: Date.parse(m.momentStart) / 1000,
        teamUrl: m.teamUrl || undefined,
        iconUrl: m.iconUrl || undefined,
        playerName,
        playerNameIn,
        cardColor: getCardColor(m.moment),
        score,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);
}

export function getHalfLabel(half) {
  switch (half) {
    case 1: return '1T';
    case 2: return '2T';
    case 3: return '1T | P';
    case 4: return '2T | P';
    case 5: return 'PEN';
    default: return '';
  }
}

export function getMinutesLabel(minutes, half) {
  if (!minutes || !half) return '';
  const baseTimes = {
    1: 45,
    2: 90,
    3: 105,
    4: 120,
  };

  const baseTime = baseTimes[half];

  if (!baseTime || minutes <= baseTime) {
    return `${minutes}'`;
  }

  return `${baseTime}'+${minutes - baseTime}'`;
}

/**
 * Short category label (not for `MATCH_STATUS` — use `getKeyMomentPrimaryLabel` with intl there).
 * `RED_CARD_SECOND_YELLOW` uses the yellow-card copy (second yellow) per product reference.
 */
export function getCategoryLabel(category, cardColor, labels, moment) {
  const l = labels ?? DEFAULT_KEY_MOMENT_LABELS;
  if (moment === 'RED_CARD_SECOND_YELLOW') {
    return l.redCard;
  }
  switch (category) {
    case KeyMomentCategory.GOAL: return l.goal;
    case KeyMomentCategory.CARD: {
      if (cardColor === CardColor.RED) return l.redCard;
      return l.yellowCard;
    }
    case KeyMomentCategory.SUBSTITUTION: return l.substitution;
    case KeyMomentCategory.MATCH_STATUS: return l.matchStatus;
    default: return '';
  }
}

/** Primary title line used on cards/preview — includes match-phase copy and penalties. */
export function getKeyMomentPrimaryLabel(item, labels, formatMessage) {
  const l = labels ?? DEFAULT_KEY_MOMENT_LABELS;
  if (!item) return '';

  if (isPenaltyMissMomentCode(item.moment)) {
    return formatMessage({
      id: 'keyMoments.moment.PENALTY_MISSED',
      defaultMessage: 'Missed penalty',
    });
  }

  if (item.category === KeyMomentCategory.MATCH_STATUS && item.moment) {
    const id = `keyMoments.moment.${canonicalKeyMomentMomentCode(item.moment)}`;
    return formatMessage({ id, defaultMessage: l.matchStatus });
  }

  return getCategoryLabel(item.category, item.cardColor, l, item.moment);
}

/**
 * Penalty shootout (half `5`): hide match clock — score-only line per product ref.
 */
export function shouldHideKeyMomentClock(item) {
  if (!item || item.category !== KeyMomentCategory.GOAL) return false;
  return item.half === 5;
}

export function getKeyMomentUiModifier(item) {
  if (!item) return '';

  if (item.category === KeyMomentCategory.CARD && item.cardColor) {
    if (item.moment === 'RED_CARD_SECOND_YELLOW') return 'card-red';
    return `card-${item.cardColor}`;
  }
  if (isPenaltyMissMomentCode(item.moment)) return 'penalty-missed';
  if (item.category === KeyMomentCategory.MATCH_STATUS) return 'match_status';
  return item.category;
}

/** Max visible characters for substitution player names in carousel / bar preview (layout only). */
export const KEY_MOMENT_SUBSTITUTION_PLAYER_NAME_MAX_CHARS = 28;

/**
 * Shortens substitution player names in tight layouts; API / selection data stay unchanged.
 */
export function truncateKeyMomentSubstitutionPlayerName(
  name,
  maxChars = KEY_MOMENT_SUBSTITUTION_PLAYER_NAME_MAX_CHARS,
) {
  if (name == null) return '';
  const s = String(name).trim();
  if (s.length === 0) return '';
  if (s.length <= maxChars) return s;
  if (maxChars < 2) return '…';
  return `${s.slice(0, maxChars - 1)}…`;
}
